import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Wrench, ListChecks, Sparkles, RefreshCw, Search, AlertTriangle, Building2 } from "lucide-react";
import { useAdminT } from "@/contexts/AdminLanguageContext";

/**
 * Prompt 19 — Plan SEO panel (roadmap of 50 objectives + Business Facts + Technical Actions).
 * NEVER publishes anything. Hands each blueprint back to the existing Studio Create/Improve modes.
 */

const TIER_COLOR = {
  VERY_HIGH: "text-[#ff9a3c] border-[#ff9a3c]",
  HIGH: "text-[#66d18a] border-[#66d18a]",
  MEDIUM: "text-[#C9A55A] border-[#C9A55A]",
  LOW: "text-[#8A8A8A] border-[#8A8A8A]",
};

const TYPE_LABELS_ES = {
  NEW_PAGE: "Nueva página",
  IMPROVE_EXISTING: "Mejorar existente",
  HOLD: "En espera",
  ALL: "Todas",
};

const TIER_LABELS_ES = {
  VERY_HIGH: "MUY ALTA",
  HIGH: "ALTA",
  MEDIUM: "MEDIA",
  LOW: "BAJA",
};

function BlueprintRow({ bp, onGenerate, onImprove }) {
  const gsc = bp.gsc || {};
  const noData = gsc.noData;
  const strategicTier = bp.strategicTier || bp.opportunityTier || "LOW";
  const gscTier = bp.gscEvidenceTier || bp.opportunityTier || "LOW";
  const overallTier = bp.overallTier || bp.opportunityTier || "LOW";
  const related = bp.relatedEvidence || {};
  const landings = bp.currentLandings || [];
  return (
    <div className="border border-[#2A2A2A] p-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2" data-testid={`bp-row-${bp.id}`}>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${TIER_COLOR[overallTier] || TIER_COLOR.LOW}`}
                title={(bp.scoreReasons || []).join(" · ")}>
            GLOBAL {TIER_LABELS_ES[overallTier] || overallTier} · {bp.overallScore ?? bp.opportunityScore}
          </span>
          <span className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${TIER_COLOR[strategicTier] || TIER_COLOR.LOW}`}
                title="Score estratégico comercial — potencial de ingresos independiente de GSC.">
            ESTRAT {TIER_LABELS_ES[strategicTier] || strategicTier} · {bp.strategicScore ?? bp.opportunityScore}
          </span>
          <span className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${TIER_COLOR[gscTier] || TIER_COLOR.LOW}`}
                title="Score de evidencia GSC — señal directa + related queries.">
            GSC {TIER_LABELS_ES[gscTier] || gscTier} · {bp.gscEvidenceScore ?? bp.opportunityScore}
          </span>
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A]">{bp.clusterName}</span>
          <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 border ${bp.type === "HOLD" ? "border-[#e57373] text-[#e57373]" : bp.type === "IMPROVE_EXISTING" ? "border-[#8ab6ff] text-[#8ab6ff]" : "border-[#C9A55A] text-[#C9A55A]"}`}>
            {TYPE_LABELS_ES[bp.type] || bp.type.replace("_"," ")}
          </span>
          {bp.protectCurrentSeo && (
            <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 border border-[#66d18a] text-[#66d18a]" title="Protege SEO actual — la página ya rankea; solo refresh, no rewrite.">PROTEGER SEO</span>
          )}
          {bp.commercial && <span className="text-[9px] text-[#C9A55A] uppercase">comercial</span>}
          {bp.cannibalizationRisk === "HIGH" && <span className="text-[9px] text-[#e57373] uppercase">riesgo canibalización</span>}
        </div>
        <div className="text-[#F5F1EB] mt-1">
          <span className="text-[#8A8A8A] text-[10px] mr-1">#{bp.index}</span>{bp.name}
        </div>
        <div className="text-[11px] text-[#8A8A8A]">
          kw: <span className="text-[#F5F1EB]">{bp.primaryKeyword}</span> · mercado: {bp.market}
        </div>
        <div className="text-[11px] text-[#8A8A8A]">
          {noData ? (
            related.hasRelatedEvidence ? (
              <span className="text-[#8ab6ff]" title="Sin datos GSC directos, pero hay evidencia relacionada.">
                SIN DATOS GSC DIRECTOS · Evidencia relacionada: {related.queryCount} queries · {related.totalImpressions} impr · {related.totalClicks} clicks
              </span>
            ) : (
              <span>SIN DATOS GSC DIRECTOS · sin evidencia relacionada aún</span>
            )
          ) : (
            <>impr {gsc.impressions} · clicks {gsc.clicks} · CTR {gsc.ctr}% · pos {gsc.position}
             {gsc.actualQuery && gsc.actualQuery !== bp.primaryKeyword && <> · query real: <span className="text-[#F5F1EB]">{gsc.actualQuery}</span></>}
             {related.hasRelatedEvidence && <> · related: {related.queryCount}q/{related.totalImpressions}i</>}
            </>
          )}
        </div>
        {bp.existingGuideTitle && (
          <div className="text-[11px] text-[#8ab6ff]">
            Ya existe: {bp.existingGuideTitle} <span className="text-[#8A8A8A]">/{bp.existingGuideSlug}</span>
          </div>
        )}
        {landings.length > 0 && bp.type === "NEW_PAGE" && (
          <div className="text-[11px] text-[#8A8A8A] mt-1">
            <span className="text-[#F5F1EB]">Landings actuales que reciben la demanda:</span>{" "}
            {landings.slice(0,3).map((ld,i) => (
              <span key={i} className="mr-2">
                <span className="text-[9px] uppercase text-[#8A8A8A] mr-1">[{ld.role}]</span>
                <a href={ld.url} target="_blank" rel="noopener noreferrer" className="text-[#8ab6ff] underline">{new URL(ld.url).pathname}</a>
                <span className="text-[#8A8A8A]"> · {ld.impressions}i</span>
              </span>
            ))}
          </div>
        )}
        {bp.hold && bp.holdReason && (
          <div className="text-[11px] text-[#e57373] mt-1"><AlertTriangle size={10} className="inline mr-1"/>{bp.holdReason}</div>
        )}
        {(bp.scoreReasons || []).length > 0 && (
          <details className="text-[10px] text-[#8A8A8A] mt-1">
            <summary className="cursor-pointer hover:text-[#F5F1EB]">Por qué este score</summary>
            <ul className="mt-1 space-y-0.5 pl-4 list-disc list-inside">
              {bp.scoreReasons.map((r,i) => <li key={i}>{r}</li>)}
            </ul>
          </details>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-start md:items-center">
        {bp.type === "IMPROVE_EXISTING" ? (
          <button
            onClick={() => onImprove(bp)}
            className="text-[10px] uppercase tracking-[0.16em] text-[#8ab6ff] border border-[#8ab6ff]/50 px-2 py-1 hover:bg-[#8ab6ff] hover:text-[#0C0C0C]"
            data-testid={`bp-improve-${bp.id}`}
          >
            {bp.protectCurrentSeo ? "Mejorar con cuidado" : "Mejorar página"}
          </button>
        ) : bp.type === "HOLD" ? (
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A]">En espera</span>
        ) : (
          <button
            onClick={() => onGenerate(bp)}
            className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] border border-[#C9A55A]/50 px-2 py-1 hover:bg-[#C9A55A] hover:text-[#0C0C0C]"
            data-testid={`bp-generate-${bp.id}`}
          >
            Generar contenido
          </button>
        )}
      </div>
    </div>
  );
}

function BusinessFactsEditor({ facts, confirmedKeys, onSave }) {
  const [json, setJson] = useState(JSON.stringify(facts, null, 2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    try {
      const parsed = JSON.parse(json);
      setBusy(true); setErr("");
      await onSave(parsed);
    } catch (e) {
      setErr(e.message || "Invalid JSON");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#2A2A2A] p-4" data-testid="business-facts-editor">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-overline text-[#C9A55A] flex items-center gap-2"><Building2 size={14}/> Business Facts</h3>
        <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.16em]">
          {confirmedKeys.length} confirmed keys
        </span>
      </div>
      <p className="text-[11px] text-[#8A8A8A] mb-2">
        Fuente central de verdad usada por AI SEO Studio y Factual QA. Edita el JSON, luego guarda. Marca `airportDelivery.MJV.available` como true SÓLO cuando el servicio en Murcia esté realmente confirmado.
      </p>
      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        rows={16}
        spellCheck={false}
        className="w-full bg-[#0C0C0C] border border-[#2A2A2A] text-[#F5F1EB] text-[12px] font-mono p-3 focus:border-[#C9A55A]"
        data-testid="business-facts-json"
      />
      {err && <div className="text-[11px] text-[#e57373] mt-1">{err}</div>}
      <button onClick={save} disabled={busy} className="btn-gold mt-2" data-testid="business-facts-save">
        {busy ? <Loader2 size={12} className="inline animate-spin mr-1"/> : null} Guardar
      </button>
    </div>
  );
}

function TechnicalActions({ actions }) {
  const sev = { HIGH: "text-[#e57373] border-[#e57373]", MEDIUM: "text-[#C9A55A] border-[#C9A55A]", LOW: "text-[#8A8A8A] border-[#8A8A8A]" };
  const SEV_ES = { HIGH: "ALTA", MEDIUM: "MEDIA", LOW: "BAJA" };
  return (
    <div data-testid="tech-actions">
      <h3 className="text-overline text-[#C9A55A] mb-2 flex items-center gap-2"><Wrench size={14}/> Acciones técnicas SEO</h3>
      <div className="space-y-2">
        {actions.map((a) => (
          <div key={a.id} className="border border-[#2A2A2A] p-3">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${sev[a.severity]}`}>{SEV_ES[a.severity] || a.severity}</span>
              <span className="text-[#F5F1EB]">{a.issue}</span>
              {a.manual && <span className="text-[9px] text-[#e57373] uppercase">requiere acción manual</span>}
            </div>
            <div className="text-[11px] text-[#8A8A8A] mt-1">Evidencia: {a.evidence}</div>
            <div className="text-[11px] text-[#F5F1EB] mt-1">Cómo resolver: {a.recommendedFix}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SeoPlanPanel({ onSendToImprove, onSendToCreate }) {
  const { t } = useAdminT();
  const [tab, setTab] = useState("plan"); // "plan" | "facts" | "tech"
  const [blueprints, setBlueprints] = useState([]);
  const [counts, setCounts] = useState({ byType: {}, byTier: {} });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [facts, setFacts] = useState(null);
  const [confirmedKeys, setConfirmedKeys] = useState([]);
  const [actions, setActions] = useState([]);
  const [sourceStatus, setSourceStatus] = useState(null);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: plan }, { data: bf }, { data: ta }, { data: sources }] = await Promise.all([
        api.get("/admin/seo-plan"),
        api.get("/admin/seo-plan/business-facts"),
        api.get("/admin/seo-plan/technical-actions"),
        api.get("/admin/ai-seo/intelligence/status"),
      ]);
      setBlueprints(plan.blueprints || []);
      setCounts({ byType: plan.byType, byTier: plan.byTier });
      setFacts(bf.facts);
      setConfirmedKeys(bf.confirmedKeys || []);
      setActions(ta.actions || []);
      setSourceStatus(sources);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const initSeed = async () => {
    setSeeding(true);
    try {
      await api.post("/admin/seo-plan/init");
      await load();
    } finally { setSeeding(false); }
  };

  const saveFacts = async (newFacts) => {
    await api.put("/admin/seo-plan/business-facts", { facts: newFacts });
    setFacts(newFacts);
  };

  const generate = async (bp) => {
    const { data } = await api.get(`/admin/seo-plan/${bp.id}/handoff`);
    onSendToCreate({
      topic: bp.name,
      focusKeyword: bp.primaryKeyword,
      category: "guide",
      blueprintId: bp.id,
      notes: `Blueprint #${bp.index} · ${bp.clusterName} · ${bp.market} (Blueprint Intent Lock enforced). GSC: ${JSON.stringify(bp.gsc)}. Strategic ${bp.strategicTier || bp.opportunityTier} · GSC evidence ${bp.gscEvidenceTier || bp.opportunityTier}. Related queries: ${JSON.stringify((data.relatedQueries||[]).slice(0,5).map(r => r.query))}. Suggested internal links: ${JSON.stringify(data.suggestedInternalLinks)}. Content brief: Answer-first (40-80w) → Key Facts → Own fleet · Relevant fleet · Local service · Delivery (only if confirmed) · Booking · FAQ 5-10 grounded on Business Facts · Trust · CTA.`,
    });
  };
  const improve = async (bp) => {
    const { data } = await api.get(`/admin/seo-plan/${bp.id}/handoff`);
    onSendToImprove({
      guideId: bp.existingGuideId,
      query: bp.name,
      focusKeyword: bp.primaryKeyword,
      notes: `Blueprint #${bp.index} improve. Existing guide slug: ${bp.existingGuideSlug}. Business Facts loaded. Related queries: ${JSON.stringify(data.relatedQueries.slice(0,5).map(r => r.query))}. Suggested internal links: ${JSON.stringify(data.suggestedInternalLinks)}. GSC: ${JSON.stringify(bp.gsc)}. Update angle: add Answer-first block, key facts (own fleet, service areas, delivery), local details, FAQ grounded on Business Facts.`,
    });
  };

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return blueprints.filter((b) =>
      (typeFilter === "ALL" || b.type === typeFilter) &&
      (!q || b.name.toLowerCase().includes(q) || b.primaryKeyword.includes(q) || b.market.toLowerCase().includes(q))
    );
  }, [blueprints, filter, typeFilter]);

  const done = blueprints.filter((b) => b.status === "PUBLISHED").length;

  if (loading) return <div className="text-[#8A8A8A]">{t("common.loading")}</div>;

  return (
    <div data-testid="seo-plan-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl text-[#F5F1EB] flex items-center gap-2">
            <ListChecks size={20} className="text-[#C9A55A]" /> Plan SEO
          </h2>
          <p className="text-xs text-[#8A8A8A] mt-1">
            Roadmap interno de 50 objetivos. Ninguno se publica automáticamente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.16em]">{done}/{blueprints.length} publicados</span>
          <button
            onClick={initSeed}
            disabled={seeding}
            className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] border border-[#C9A55A]/50 px-2 py-1 hover:bg-[#C9A55A] hover:text-[#0C0C0C]"
            data-testid="seo-plan-refresh-seed"
            title="Recompute GSC + Improve/New classification"
          >
            {seeding ? <Loader2 size={10} className="inline animate-spin mr-1"/> : <RefreshCw size={10} className="inline mr-1"/>} Refrescar GSC
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#2A2A2A] mb-4" data-testid="plan-sub-tabs">
        {[
          { key: "plan", label: "Plan · 50 objetivos" },
          { key: "facts", label: "Business Facts" },
          { key: "tech", label: "Acciones técnicas" },
          { key: "sources", label: "Fuentes y procedencia" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            className={`px-3 h-9 text-[10px] uppercase tracking-[0.18em] border-b-2 ${tab === s.key ? "border-[#C9A55A] text-[#C9A55A]" : "border-transparent text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
            data-testid={`plan-tab-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {tab === "plan" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px] text-[#8A8A8A]">
            <div className="flex-1 min-w-[200px] flex items-center gap-2 border border-[#2A2A2A] px-2 py-1">
              <Search size={12}/>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrar por nombre, keyword o mercado…"
                className="bg-transparent border-none outline-none text-sm text-[#F5F1EB] w-full"
                data-testid="plan-filter"
              />
            </div>
            {["ALL", "NEW_PAGE", "IMPROVE_EXISTING", "HOLD"].map((f) => (
              <button
                key={f}
                onClick={() => setTypeFilter(f)}
                className={`px-2 py-1 border ${typeFilter === f ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A]"} uppercase tracking-[0.16em]`}
                data-testid={`plan-type-${f}`}
              >
                {TYPE_LABELS_ES[f] || f.replace("_", " ")} {counts.byType[f] != null ? `· ${counts.byType[f]}` : ""}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {filtered.map((bp) => (
              <BlueprintRow key={bp.id} bp={bp} onGenerate={generate} onImprove={improve} />
            ))}
            {filtered.length === 0 && <div className="text-[#8A8A8A] text-sm">No hay objetivos con este filtro.</div>}
          </div>
        </>
      )}

      {tab === "facts" && facts && (
        <BusinessFactsEditor facts={facts} confirmedKeys={confirmedKeys} onSave={saveFacts} />
      )}

      {tab === "tech" && (
        <TechnicalActions actions={actions} />
      )}
      {tab === "sources" && sourceStatus && (
        <div className="space-y-3" data-testid="seo-source-status">
          <div className="border border-[#2A2A2A] p-4">
            <h3 className="text-overline text-[#C9A55A] mb-2">Strategy Profile</h3>
            <p className="text-sm text-[#F5F1EB]">Prioridad: {sourceStatus.strategyProfile.primaryMarket} · {sourceStatus.strategyProfile.objective}</p>
            <p className="text-[11px] text-[#8A8A8A] mt-1">Las recomendaciones nunca crean ni publican páginas automáticamente.</p>
          </div>
          {Object.entries(sourceStatus.sources || {}).map(([key, source]) => (
            <div key={key} className="border border-[#2A2A2A] p-3 flex items-center justify-between gap-3">
              <span className="text-sm text-[#F5F1EB]">{key}</span>
              <span className={`text-[10px] uppercase tracking-[0.16em] border px-2 py-1 ${source.provenance === "REAL" ? "text-[#66d18a] border-[#66d18a]" : source.provenance === "INFERRED" ? "text-[#C9A55A] border-[#C9A55A]" : "text-[#e57373] border-[#e57373]"}`}>
                {source.provenance}{source.state ? ` · ${source.state}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
